import React, { useState, useMemo } from 'react';

interface Column<T> {
  header: string | React.ReactNode;
  accessor: keyof T;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  maxWidth?: string;
  key?: string;
  /** Si true, la celda no usa overflow-hidden (p. ej. menús/popovers absolutos dentro de la celda) */
  cellOverflowVisible?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  className?: string;
  emptyMessage?: string | React.ReactNode;
  isLoading?: boolean;
  selectedRow?: T | null;
  getRowId?: (row: T) => string | number;
}

type SortDirection = 'asc' | 'desc' | null;

const DataTable = <T extends object>({
  data,
  columns,
  onRowClick,
  className = '',
  emptyMessage = 'No hay datos disponibles',
  isLoading = false,
  selectedRow = null,
  getRowId,
}: DataTableProps<T>) => {
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Función para obtener ID único de fila
  const getRowKey = (row: T, index: number): string | number => {
    if (getRowId) return getRowId(row);
    return `row-${index}`;
  };

  // Función de ordenamiento
  const handleSort = (column: Column<T>) => {
    if (!column.sortable) return;

    const accessor = column.accessor;
    
    if (sortColumn === accessor) {
      // Cambiar dirección o desactivar ordenamiento
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(accessor);
      setSortDirection('asc');
    }
  };

  // Datos ordenados
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aString.localeCompare(bString);
      } else {
        return bString.localeCompare(aString);
      }
    });
  }, [data, sortColumn, sortDirection]);

  // Verificar si una fila está seleccionada
  const isRowSelected = (row: T): boolean => {
    if (!selectedRow) return false;
    if (getRowId) {
      return getRowId(row) === getRowId(selectedRow);
    }
    return row === selectedRow;
  };

  // Renderizar icono de ordenamiento
  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;

    const isActive = sortColumn === column.accessor;
    
    if (!isActive) {
      return (
        <span className="text-gray-400 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-110">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13l3-3m0 0l3 3M6 10v8m0-8H3m15 0h-3m-3 0l3 3m0 0l3-3m-3 3V6" />
          </svg>
        </span>
      );
    }

    return (
      <span className={`transition-all duration-200 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
        {sortDirection === 'asc' ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </span>
    );
  };

  // Estado de carga
  if (isLoading) {
    return (
      <div className={`${className} flex items-center justify-center py-16`}>
        <div className="text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-blue-100 animate-ping opacity-75"></div>
            <div className="relative inline-block animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="mt-4 text-sm font-medium text-gray-700">Cargando datos...</p>
          <p className="mt-1 text-xs text-gray-500">Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  // Estado vacío
  if (sortedData.length === 0) {
    return (
      <div className={`${className} flex items-center justify-center py-16`} role="status" aria-live="polite">
        <div className="text-center max-w-sm">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="text-base font-medium text-gray-900">
            {typeof emptyMessage === 'string' ? emptyMessage : emptyMessage}
          </p>
          <p className="mt-1 text-sm text-gray-500">Intenta ajustar los filtros o realizar una nueva búsqueda</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm`}>
      <div className="">
        <table
          className="w-full border-collapse table-fixed"
          role="table"
          aria-label="Tabla de datos"
        >
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {columns.map((column, index) => {
                const isSortable = column.sortable === true;
                const isActive = sortColumn === column.accessor;
                
                return (
                  <th
                    key={`header-${column.key || column.accessor as string}-${index}`}
                    scope="col"
                    className={`
                      px-2.5 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider
                      first:rounded-tl-lg last:rounded-tr-lg
                      ${isSortable ? 'cursor-pointer select-none group hover:bg-gray-100 transition-all duration-200 active:bg-gray-200' : ''}
                      ${isActive ? 'bg-blue-50 text-custom-blue' : ''}
                    `}
                    style={{
                      width: column.width || 'auto',
                      maxWidth: column.maxWidth ? column.maxWidth : undefined
                    }}
                    onClick={() => handleSort(column)}
                    aria-sort={
                      isSortable
                        ? isActive
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.header}</span>
                      {renderSortIcon(column)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {sortedData.map((row, rowIndex) => {
              const rowKey = getRowKey(row, rowIndex);
              const isSelected = isRowSelected(row);
              const isEven = rowIndex % 2 === 0;
              
              return (
                <tr
                  key={rowKey}
                  onClick={() => onRowClick?.(row)}
                  className={`
                    transition-all duration-200 ease-in-out
                    ${onRowClick ? 'cursor-pointer' : ''}
                    ${isSelected 
                      ? 'bg-blue-50 border-l-4 border-l-blue-500 shadow-sm' 
                      : isEven 
                        ? 'bg-white hover:bg-gray-50/80' 
                        : 'bg-gray-50/30 hover:bg-gray-100/60'
                    }
                    ${onRowClick && !isSelected ? 'hover:shadow-sm hover:scale-[1.001]' : ''}
                  `}
                  role="row"
                  aria-selected={isSelected}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                >
                  {columns.map((column, colIndex) => {
                    const overflowVisible = column.cellOverflowVisible === true
                    return (
                    <td
                      key={`cell-${column.key || column.accessor as string}-${rowKey}-${colIndex}`}
                      className={`
                        min-w-0 px-2.5 py-2 text-sm text-gray-700 align-top
                        ${overflowVisible ? 'overflow-visible' : 'overflow-hidden'}
                        ${isSelected ? 'font-medium' : ''}
                      `}
                    style={{
                      width: column.width || 'auto',
                      maxWidth: column.maxWidth || undefined
                    }}
                    role="gridcell"
                  >
                      <div
                        className={
                          overflowVisible
                            ? 'min-w-0 max-w-full overflow-visible'
                            : 'min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap'
                        }
                      >
                        {column.cell ? column.cell(row) : String(row[column.accessor] ?? '')}
                      </div>
                    </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable; 